import { useState } from "react";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, Video, ExternalLink, Mic, PlayCircle, Film } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtEventDate, isPastEvent } from "@/lib/image";

const ytId = (u) => { const m = (u || "").match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/); return m ? m[1] : ""; };
const vimeoId = (u) => { const m = (u || "").match(/vimeo\.com\/(?:video\/)?(\d+)/); return m ? m[1] : ""; };

function InlinePlayer({ ev }) {
  const type = ev.recording_type || (ev.recording_url ? "youtube" : "");
  if (type === "file" && ev.recording_url) {
    const src = ev.recording_url.startsWith("http") ? ev.recording_url : `${process.env.REACT_APP_BACKEND_URL}${ev.recording_url}`;
    return <video controls autoPlay controlsList="nodownload" className="w-full h-full"><source src={src} /></video>;
  }
  const vm = vimeoId(ev.recording_url);
  if (type === "vimeo" || vm) { if (vm) return <iframe title={ev.title} src={`https://player.vimeo.com/video/${vm}?autoplay=1`} allow="autoplay; fullscreen" allowFullScreen className="w-full h-full" />; }
  const yt = ytId(ev.recording_url);
  if (yt) return <iframe title={ev.title} src={`https://www.youtube.com/embed/${yt}?autoplay=1`} allow="autoplay; encrypted-media; fullscreen" allowFullScreen className="w-full h-full" />;
  return <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm">Recording not available.</div>;
}

export function MyEventsDialog({ open, onOpenChange, events, onChanged }) {
  const [playingId, setPlayingId] = useState(null);

  const cancel = async (id) => {
    try { await api.delete(`/events/${id}/register`); toast.success("Registration cancelled"); await onChanged?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const recordings = events.filter((e) => e.event_type === "recorded");
  const live = events.filter((e) => e.event_type !== "recorded");
  const upcoming = live.filter((e) => !isPastEvent(e.starts_at));
  const past = live.filter((e) => isPastEvent(e.starts_at));

  const LiveRow = ({ ev, isPast }) => (
    <div className="border border-border rounded-lg p-4" data-testid={`my-event-${ev.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display font-semibold text-navy">{ev.title}</p>
          {ev.speaker && <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><Mic className="h-3 w-3 text-gold" /> {ev.speaker}</p>}
          <p className="text-sm text-slate-600 mt-1 flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-gold" /> {fmtEventDate(ev.starts_at)}</p>
          {ev.duration && <p className="text-sm text-slate-500 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-gold" /> {ev.duration}</p>}
          {ev.mode === "offline" && ev.location && <p className="text-sm text-slate-500 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-gold" /> {ev.location}</p>}
        </div>
        <span className={`shrink-0 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${ev.mode === "online" ? "bg-emerald-100 text-emerald-700" : "bg-gold/20 text-gold"}`}>{ev.mode === "online" ? "Online" : "In-person"}</span>
      </div>
      {!isPast && ev.mode === "online" && ev.join_link && (
        <a href={ev.join_link} target="_blank" rel="noreferrer" data-testid={`join-link-${ev.id}`}
          className="mt-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-navy text-white text-sm font-semibold hover:bg-navy-light transition-colors">
          <Video className="h-4 w-4" /> Join session <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
      {!isPast && (
        <button onClick={() => cancel(ev.id)} data-testid={`my-cancel-event-${ev.id}`} className="ml-3 mt-3 text-sm text-slate-500 hover:text-red-600 transition-colors">Cancel registration</button>
      )}
    </div>
  );

  const RecRow = ({ ev }) => (
    <div className="border border-border rounded-lg p-4" data-testid={`my-recording-${ev.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display font-semibold text-navy">{ev.title}</p>
          {ev.speaker && <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><Mic className="h-3 w-3 text-gold" /> {ev.speaker}</p>}
          {ev.duration && <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-gold" /> {ev.duration}</p>}
        </div>
        <span className="shrink-0 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-navy text-gold">Recorded</span>
      </div>
      {playingId === ev.id ? (
        <div className="mt-3 aspect-video w-full bg-black rounded-md overflow-hidden"><InlinePlayer ev={ev} /></div>
      ) : (
        <button onClick={() => setPlayingId(ev.id)} data-testid={`watch-recording-${ev.id}`}
          className="mt-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-navy text-white text-sm font-semibold hover:bg-navy-light transition-colors">
          <PlayCircle className="h-4 w-4" /> Watch recording
        </button>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setPlayingId(null); onOpenChange(o); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="my-events-dialog">
        <DialogHeader><DialogTitle>My Recordings & Events</DialogTitle></DialogHeader>
        {events.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="text-slate-500 mt-3">You haven't registered for any events or bought any recordings yet.</p>
            <a href="/events" className="inline-block mt-4 text-sm font-semibold text-navy hover:text-gold">Browse events & recordings →</a>
          </div>
        ) : (
          <div className="space-y-5">
            {recordings.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold flex items-center gap-1.5"><Film className="h-3.5 w-3.5 text-gold" /> My Recordings</p>
                {recordings.map((ev) => <RecRow key={ev.id} ev={ev} />)}
              </div>
            )}
            {upcoming.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Upcoming Live</p>
                {upcoming.map((ev) => <LiveRow key={ev.id} ev={ev} />)}
              </div>
            )}
            {past.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Past Live</p>
                {past.map((ev) => <LiveRow key={ev.id} ev={ev} isPast />)}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
